import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import SaveIcon from '@mui/icons-material/Save';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CancelIcon from '@mui/icons-material/Cancel';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { fetchData } from '../../helpers/apiUtils';
import {
  CampaignWithEmails,
  MailingType,
  Webinar,
  WebinarClient,
  EmailFormData,
  WebinarMailingEmail,
} from '../../helpers/webinarMailingData';
import TinyMCEEditor, { TinyMCEEditorRef } from '../../components/TinyMCEEditor';
import PlaceholderLegend from '../../components/PlaceholderLegend';
import { PLACEHOLDERS, validateEmail, ValidationResult } from './htmlValidator';
import SpamScorePanel, { CombinedResult as SpamScoreResult } from '../../components/SpamScorePanel';
import { recordDecision } from '../../helpers/spamWords/spamScoreApi';
import { containsHardcodedYear, YEAR_WARNING } from '../../helpers/mailingValidation';
import RescheduleModal from './RescheduleModal';
import ScheduleConfirmationDialog, {
  MailingPolicyTable,
} from '../../components/ScheduleConfirmationDialog';
import NonOpenerSection from '../../components/NonOpenerSection';
import Editor from '@monaco-editor/react';
import moment from 'moment-timezone';
import { formatGermanTime, germanNow, toGermanDatetimeLocal } from '../../helpers/germanTime';

// Max new heat-up emails per webinar campaign. Existing campaigns with more
// emails remain editable (backend still accepts up to 5), but the UI prevents
// adding new ones beyond this cap.
const MAX_EMAILS_PER_CAMPAIGN = 4;

interface CampaignEditorProps {
  campaignId: number | null;
  onSaved: () => void;
  onCancel: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Default email - mailing_type is now per-email, defaults to 'heatup'
const getDefaultEmail = (
  clients: WebinarClient[],
  mailingType: MailingType = 'heatup',
): EmailFormData => ({
  mailing_number: 1,
  subject: '',
  subject_b: '',
  from_name: 'Finanztrends',
  preview_text: '',
  html_body: '',
  use_relative_date: true,
  days_before_webinar: 7,
  static_send_datetime: null,
  send_time: '09:00',
  use_client_from_names: false,
  client_from_names: null,
  selected_clients:
    mailingType === 'redlink'
      ? ['ft'] // Redlink: only Finanztrends
      : clients.filter((c) => c.active).map((c) => c.code), // Heatup: all active clients
  mailing_type: mailingType,
  auto_non_opener: false,
  auto_non_opener_time: '19:00',
  auto_non_opener_clients: null,
});

type EditorMode = 'wysiwyg' | 'code';

const CampaignEditor: React.FC<CampaignEditorProps> = ({ campaignId, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Set by executeWebinarSchedule when the backend skipped some (email, client) pairs, read by
  // the schedule dialog's onSuccess. A ref, not state: onSuccess fires in the same tick as the
  // await that sets this, so a state value would still be the previous render's.
  const skippedNoteRef = React.useRef<string | null>(null);

  // Local campaign ID state - tracks ID after creation (prop doesn't update)
  const [localCampaignId, setLocalCampaignId] = useState<number | null>(campaignId);

  // Editor mode and validation state
  const [editorMode, setEditorMode] = useState<EditorMode>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('webinar-mailing-editor-mode');
    return saved === 'code' || saved === 'wysiwyg' ? saved : 'wysiwyg';
  });
  const [validation, setValidation] = useState<ValidationResult>({
    valid: true,
    errors: [],
    warnings: [],
  });
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [spamResults, setSpamResults] = useState<(SpamScoreResult | null)[]>([]);
  const [spamOverrideOpen, setSpamOverrideOpen] = useState(false);
  const [spamOverridePayload, setSpamOverridePayload] = useState<{
    emailIndex: number;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const [spamAdvisoryOpen, setSpamAdvisoryOpen] = useState(false);
  const [spamAdvisoryPayload, setSpamAdvisoryPayload] = useState<{
    emailIndex: number;
    warningCount: number;
    llmChecked: boolean;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const handleSpamResultChange = useCallback((index: number, result: SpamScoreResult) => {
    setSpamResults((prev) => {
      const next = [...prev];
      next[index] = result;
      return next;
    });
  }, []);

  // Track which emails have imported HTML (per-email index)
  const [importedEmails, setImportedEmails] = useState<Set<number>>(new Set());
  const [showWysiwygWarning, setShowWysiwygWarning] = useState(false);
  const [deleteEmailIndex, setDeleteEmailIndex] = useState<number | null>(null);
  const [sentEmailIndices, setSentEmailIndices] = useState<Set<number>>(new Set());
  const [scheduledEmailIndices, setScheduledEmailIndices] = useState<Set<number>>(new Set());

  // Editor refs for placeholder insertion (array for multiple email tabs)
  const editorRefs = useRef<(TinyMCEEditorRef | null)[]>([]);
  const monacoRef = useRef<any>(null);

  // Campaign data
  const [name, setName] = useState('');
  const [webinarId, setWebinarId] = useState('');
  const [occurrenceDate, setOccurrenceDate] = useState('');
  const [emails, setEmails] = useState<EmailFormData[]>([]);
  const [activeEmailTab, setActiveEmailTab] = useState(0);

  // Reference data
  const [webinars, setWebinars] = useState<Webinar[]>([]);
  const [clients, setClients] = useState<WebinarClient[]>([]);
  const [mailingPolicy, setMailingPolicy] = useState<MailingPolicyTable | null>(null);
  const [minSendTime, setMinSendTime] = useState<Record<string, string> | null>(null);
  const [deferredHeatupClients, setDeferredHeatupClients] = useState<string[] | null>(null);

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importedContent, setImportedContent] = useState<string | null>(null);
  const [detectedUrls, setDetectedUrls] = useState<string[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cancel schedules state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelScope, setCancelScope] = useState<'email' | 'campaign'>('email');
  const [cancelEmailId, setCancelEmailId] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Sync localCampaignId from prop (when prop changes, e.g., navigating to different campaign)
  useEffect(() => {
    setLocalCampaignId(campaignId);
  }, [campaignId]);

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [webinarRes, clientRes] = await Promise.all([
          fetchData<Webinar[]>('/api/webinar-mailings/webinars'),
          fetchData<WebinarClient[]>('/api/webinar-mailings/clients'),
        ]);

        if (webinarRes.success && webinarRes.data) {
          setWebinars(webinarRes.data);
        }
        if (clientRes.success && clientRes.data) {
          setClients(clientRes.data);
          // Fan-out policy travels with the client list (2026-09-10) — the
          // confirmation dialog renders the same table the backend enforces.
          const extra = clientRes as unknown as {
            policy?: MailingPolicyTable;
            min_send_time?: Record<string, string>;
            deferred_heatup_clients?: string[];
          };
          setMailingPolicy(extra.policy ?? null);
          setMinSendTime(extra.min_send_time ?? null);
          setDeferredHeatupClients(extra.deferred_heatup_clients ?? null);
          // Initialize default email only if no campaign is being loaded (new campaign)
          if (!campaignId) {
            setEmails([getDefaultEmail(clientRes.data, 'heatup')]);
          }
        }
      } catch (err) {
        console.error('Error loading reference data:', err);
      }
    };

    loadReferenceData();
  }, [campaignId]);

  // Load existing campaign
  useEffect(() => {
    if (campaignId) {
      loadCampaign(campaignId);
    }
  }, [campaignId]);

  const loadCampaign = async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchData<CampaignWithEmails>(`/api/webinar-mailings/campaigns/${id}`);
      if (response.success && response.data) {
        const campaign = response.data;
        setName(campaign.name);
        setWebinarId(campaign.webinar_id);
        if (campaign.occurrence_date) {
          setOccurrenceDate(moment(campaign.occurrence_date).format('YYYY-MM-DD'));
        }

        if (campaign.emails && campaign.emails.length > 0) {
          // Get default clients for fallback based on per-email type
          const activeClientCodes = clients.filter((c) => c.active).map((c) => c.code);

          // Track which emails have been sent or scheduled
          const sentIndices = new Set<number>();
          const scheduledIndices = new Set<number>();
          campaign.emails.forEach((e: WebinarMailingEmail, idx: number) => {
            if (e.status === 'sent' || e.schedule_statuses?.includes('sent')) {
              sentIndices.add(idx);
            }
            // Check for active schedules (pending or scheduled status)
            if (e.schedule_statuses) {
              const hasActiveSchedule = e.schedule_statuses.split(',').some((pair) => {
                const status = pair.split(':')[1];
                return (
                  status === 'pending' ||
                  status === 'awaiting_suppression' ||
                  status === 'scheduled'
                );
              });
              if (hasActiveSchedule) {
                scheduledIndices.add(idx);
              }
            }
          });
          setSentEmailIndices(sentIndices);
          setScheduledEmailIndices(scheduledIndices);

          setEmails(
            campaign.emails.map((e: WebinarMailingEmail) => {
              const emailType = e.mailing_type || 'heatup';
              const defaultClients = emailType === 'redlink' ? ['ft'] : activeClientCodes;
              return {
                mailing_number: e.mailing_number,
                subject: e.subject,
                subject_b: e.subject_b ?? '',
                from_name: e.from_name,
                preview_text: e.preview_text || '',
                html_body: e.html_body,
                use_relative_date: e.use_relative_date,
                days_before_webinar: e.days_before_webinar,
                static_send_datetime: e.static_send_datetime,
                send_time: e.send_time,
                use_client_from_names: e.use_client_from_names ?? false,
                client_from_names: e.client_from_names || null,
                // Parse selected_clients from email, fallback to scheduled_clients, then defaults
                selected_clients: e.selected_clients
                  ? e.selected_clients.split(',').filter(Boolean)
                  : e.scheduled_clients
                    ? e.scheduled_clients.split(',').filter(Boolean)
                    : defaultClients,
                mailing_type: emailType,
                auto_non_opener: !!e.auto_non_opener,
                auto_non_opener_time: e.auto_non_opener_time
                  ? e.auto_non_opener_time.substring(0, 5)
                  : '19:00',
                auto_non_opener_clients: e.auto_non_opener_clients
                  ? e.auto_non_opener_clients.split(',')
                  : null,
                schedule_statuses: e.schedule_statuses || null,
              };
            }),
          );
        }

        // Check if there are any scheduled items (for reschedule button visibility)
        const hasScheduled = campaign.emails?.some((e: WebinarMailingEmail) =>
          e.schedule_statuses?.includes('scheduled'),
        );
        setHasScheduledItems(hasScheduled || false);
      } else {
        setError('Fehler beim Laden der Kampagne');
      }
    } catch (err) {
      setError('Fehler beim Laden der Kampagne');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Import handlers
  const handleOpenImportModal = () => {
    setImportModalOpen(true);
    setImportError(null);
    setImportedContent(null);
    setDetectedUrls([]);
    setSelectedUrls([]);
  };

  const handleCloseImportModal = () => {
    setImportModalOpen(false);
    setImportError(null);
    setImportedContent(null);
    setDetectedUrls([]);
    setSelectedUrls([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      setImportError('Bitte eine HTML-Datei auswählen');
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      setImportError('Datei zu groß (max. 1 MB)');
      return;
    }

    setImportLoading(true);
    setImportError(null);

    try {
      // Read file content
      const htmlContent = await file.text();

      // Send to backend for extraction
      const response = await fetchData<{
        extractedContent: string;
        detectedUrls: string[];
      }>('/api/webinar-mailings/import-template', {
        method: 'POST',
        body: JSON.stringify({ htmlContent }),
      });

      if (response.success && response.data) {
        setImportedContent(response.data.extractedContent);
        setDetectedUrls(response.data.detectedUrls);
        // Select all URLs by default
        setSelectedUrls(response.data.detectedUrls);
      } else {
        setImportError(response.error || 'Fehler beim Importieren');
      }
    } catch (err: any) {
      setImportError(err.message || 'Fehler beim Importieren');
      console.error(err);
    } finally {
      setImportLoading(false);
    }
  };

  const handleToggleUrl = (url: string) => {
    setSelectedUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url],
    );
  };

  const handleConfirmImport = () => {
    if (!importedContent) return;

    let content = importedContent;

    // Strip <html>, <head>, and <body> wrapper tags - we only want inner content
    // Extract body content if present
    const bodyMatch = content.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (bodyMatch) {
      content = bodyMatch[1].trim();
    } else {
      // No body tag - strip html tag if present
      content = content.replace(/<\/?html[^>]*>/gi, '').trim();
    }
    // Remove any head section
    content = content.replace(/<head[^>]*>[\s\S]*<\/head>/gi, '').trim();

    // Replace selected URLs with {WEBINAR_LINK}
    selectedUrls.forEach((url) => {
      // Escape special regex characters in URL
      const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      content = content.replace(new RegExp(escapedUrl, 'g'), '{WEBINAR_LINK}');
    });

    // Set content directly to state
    updateEmail(activeEmailTab, 'html_body', content);

    // Mark this email as imported (for WYSIWYG warning if user switches modes)
    setImportedEmails((prev) => new Set(prev).add(activeEmailTab));

    // Close modal
    handleCloseImportModal();
    setSuccess('Template erfolgreich importiert.');
  };

  const handleSave = async (skipNavigation = false): Promise<boolean> => {
    if (!name || !webinarId) {
      setError('Bitte Name und Webinar auswählen');
      return false;
    }

    // Validate occurrence_date for recurring webinars
    const selWebinar = webinars.find((w) => w.webinar_id === webinarId);
    if (selWebinar && Boolean(selWebinar.is_recurring) && !occurrenceDate && !localCampaignId) {
      setError('Bitte Termin (Occurrence Date) auswählen');
      return false;
    }

    if (emails.some((e) => !e.subject || !e.html_body)) {
      setError('Bitte Subject und HTML-Body für alle E-Mails ausfüllen');
      return false;
    }

    // Check for hardcoded years in subject/preview_text
    for (let i = 0; i < emails.length; i++) {
      const e = emails[i];
      if (containsHardcodedYear(e.subject) || containsHardcodedYear(e.preview_text)) {
        setError(`E-Mail ${i + 1}: Jahreszahl erkannt — bitte {DATE:Y} als Platzhalter verwenden`);
        setActiveEmailTab(i);
        return false;
      }
    }

    // Validate all emails before saving
    for (let i = 0; i < emails.length; i++) {
      const emailValidation = validateEmail(emails[i].subject, emails[i].html_body);
      if (!emailValidation.valid) {
        setError(`E-Mail ${i + 1}: ${emailValidation.errors.join(', ')}`);
        setActiveEmailTab(i);
        return false;
      }
    }

    // Spam-Score hard-block check (3+ red flags, L2 not checked or <30)
    const overriddenIndexes = new Set<number>();
    for (let i = 0; i < emails.length; i++) {
      const spam = spamResults[i];
      if (spam?.shouldHardBlock) {
        setActiveEmailTab(i);
        const userConfirmed = await new Promise<boolean>((resolve) => {
          setSpamOverridePayload({ emailIndex: i, resolve });
          setSpamOverrideOpen(true);
        });
        if (!userConfirmed) {
          setError(
            `E-Mail ${i + 1}: Content-Quality-Check nicht bestanden (${spam.layer1.redCount} Red-Flags) — bitte Begriffe ersetzen.`,
          );
          return false;
        }
        overriddenIndexes.add(i);
      }
    }

    // Spam-Score advisory (non-blocking): warn if L1 warnings present and
    // either no LLM check run, stale check, or LLM score < 70.
    for (let i = 0; i < emails.length; i++) {
      const spam = spamResults[i];
      if (!spam || spam.shouldHardBlock) continue; // hard-block handled above
      const warningCount = spam.layer1.flaggedPhrases.length + spam.layer1.patternHits.length;
      if (warningCount === 0) continue;
      const llmChecked = spam.layer2Score != null && !spam.contentHashStale;
      if (llmChecked && spam.layer2Score! >= 70) continue;
      setActiveEmailTab(i);
      const userProceed = await new Promise<boolean>((resolve) => {
        setSpamAdvisoryPayload({
          emailIndex: i,
          warningCount,
          llmChecked,
          resolve,
        });
        setSpamAdvisoryOpen(true);
      });
      if (!userProceed) return false;
    }

    setSaving(true);
    setError(null);

    try {
      let savedCampaignId = localCampaignId;

      // Create or update campaign
      // Campaign mailing_type is derived from first email's type (for display purposes)
      const campaignMailingType = emails[0]?.mailing_type || 'heatup';

      if (localCampaignId) {
        const updateRes = await fetchData(`/api/webinar-mailings/campaigns/${localCampaignId}`, {
          method: 'PUT',
          body: JSON.stringify({
            name,
            mailing_type: campaignMailingType,
            webinar_id: webinarId,
          }),
        });
        if (!updateRes.success) {
          throw new Error(updateRes.error || 'Fehler beim Speichern');
        }
      } else {
        const createPayload: Record<string, any> = {
          name,
          mailing_type: campaignMailingType,
          webinar_id: webinarId,
        };
        if (occurrenceDate) {
          createPayload.occurrence_date = occurrenceDate;
        }
        const createRes = await fetchData<{ id: number }>('/api/webinar-mailings/campaigns', {
          method: 'POST',
          body: JSON.stringify(createPayload),
        });
        if (!createRes.success || !createRes.data) {
          throw new Error(createRes.error || 'Fehler beim Erstellen');
        }
        savedCampaignId = createRes.data.id;
        // Update local state so subsequent saves will UPDATE instead of CREATE
        setLocalCampaignId(savedCampaignId);
      }

      // Save emails
      const emailRes = await fetchData(
        `/api/webinar-mailings/campaigns/${savedCampaignId}/emails`,
        {
          method: 'POST',
          body: JSON.stringify({ emails }),
        },
      );

      if (!emailRes.success) {
        throw new Error(emailRes.error || 'Fehler beim Speichern der E-Mails');
      }

      // Record user_decision for each email's spam check
      emails.forEach((e, i) => {
        const spam = spamResults[i];
        if (!spam) return;
        const decision = overriddenIndexes.has(i) ? 'overridden' : 'accepted';
        recordDecision(
          'webinar_campaign',
          e.mailing_number,
          decision,
          savedCampaignId ?? null,
        ).catch((err) => console.warn('recordDecision failed', err));
      });

      if (!skipNavigation) {
        setSuccess('Kampagne erfolgreich gespeichert');
      }
      return true;
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  // Reschedule state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [hasScheduledItems, setHasScheduledItems] = useState(false);

  // Helper to get client from names for a specific email
  // Returns email's stored client_from_names or generates defaults from email's selected_clients
  const getClientFromNames = (emailIndex: number): Record<string, string> => {
    const email = emails[emailIndex];
    if (email?.client_from_names) {
      return email.client_from_names;
    }
    // Generate defaults from email's selected_clients
    const defaults: Record<string, string> = {};
    const emailClients = email?.selected_clients || [];
    emailClients.forEach((code) => {
      const client = clients.find((c) => c.code === code);
      defaults[code] = client?.name || code.toUpperCase();
    });
    return defaults;
  };

  // Update client from name for a specific email and client
  const updateClientFromName = (emailIndex: number, clientCode: string, value: string) => {
    const currentNames = getClientFromNames(emailIndex);
    const updatedNames = { ...currentNames, [clientCode]: value };
    updateEmail(emailIndex, 'client_from_names', updatedNames);
  };

  // Toggle use_client_from_names for a specific email
  const toggleUseClientFromNames = (emailIndex: number) => {
    const email = emails[emailIndex];
    const newValue = !email.use_client_from_names;
    updateEmail(emailIndex, 'use_client_from_names', newValue);
    // Initialize client_from_names if enabling and null
    if (newValue && !email.client_from_names) {
      const defaults: Record<string, string> = {};
      const emailClients = email.selected_clients || [];
      emailClients.forEach((code) => {
        const client = clients.find((c) => c.code === code);
        defaults[code] = client?.name || code.toUpperCase();
      });
      updateEmail(emailIndex, 'client_from_names', defaults);
    }
  };

  // Test email state
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testEmails, setTestEmails] = useState('');
  const [testClientCodes, setTestClientCodes] = useState<string[]>([]);
  const [sendingTest, setSendingTest] = useState(false);

  // Preview state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewWidth, setPreviewWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [previewClient, setPreviewClient] = useState<string>('bg');

  const handlePreview = async () => {
    if (!localCampaignId) {
      setError('Bitte Kampagne erst speichern');
      return;
    }

    const currentEmail = emails[activeEmailTab];
    if (!currentEmail.html_body) {
      setError('Bitte erst HTML-Inhalt eingeben');
      return;
    }

    setLoadingPreview(true);
    setError(null);

    try {
      // Save campaign first without navigation
      const saveSuccess = await handleSave(true);
      if (!saveSuccess) {
        setLoadingPreview(false);
        return;
      }

      // Fetch preview with replaced placeholders
      const response = await fetchData<{ html: string }>(
        `/api/webinar-mailings/campaigns/${localCampaignId}/preview`,
        {
          method: 'POST',
          body: JSON.stringify({
            mailing_number: currentEmail.mailing_number,
            client_code: previewClient,
          }),
        },
      );

      if (response.success && response.data) {
        setPreviewHtml(response.data.html);
        setShowPreviewDialog(true);
      } else {
        setError(response.error || 'Fehler beim Laden der Vorschau');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Vorschau');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Refetch preview when client selection changes (only while dialog is open)
  const fetchPreviewForClient = async (clientCode: string) => {
    if (!localCampaignId || !showPreviewDialog) return;

    const currentEmail = emails[activeEmailTab];
    if (!currentEmail?.mailing_number) return;

    setLoadingPreview(true);
    try {
      const response = await fetchData<{ html: string }>(
        `/api/webinar-mailings/campaigns/${localCampaignId}/preview`,
        {
          method: 'POST',
          body: JSON.stringify({
            mailing_number: currentEmail.mailing_number,
            client_code: clientCode,
          }),
        },
      );

      if (response.success && response.data) {
        setPreviewHtml(response.data.html);
      }
    } catch (err) {
      console.error('Failed to fetch preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Effect to refetch when preview client changes
  useEffect(() => {
    if (showPreviewDialog && localCampaignId && emails[activeEmailTab]) {
      fetchPreviewForClient(previewClient);
    }
  }, [previewClient, showPreviewDialog]); // Only re-fetch when client changes while dialog is open

  const handleSendTestEmail = async () => {
    // Parse comma-separated emails
    const emailList = testEmails
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    if (emailList.length === 0) {
      setError('Bitte mindestens eine E-Mail-Adresse eingeben');
      return;
    }

    // Check if campaign has content for current email
    const currentEmail = emails[activeEmailTab];
    if (!currentEmail.subject || !currentEmail.html_body) {
      setError('Bitte erst Betreff und HTML-Inhalt eingeben');
      return;
    }

    // Use clients selected in the test dialog
    if (testClientCodes.length === 0) {
      setError('Bitte mindestens einen Client für den Test auswählen.');
      return;
    }

    if (!localCampaignId) {
      setError('Bitte Kampagne erst speichern');
      return;
    }

    setSendingTest(true);
    setError(null);
    setSuccess(null);

    try {
      // Save campaign first without navigation
      const saveSuccess = await handleSave(true);
      if (!saveSuccess) {
        // handleSave already set the error
        setSendingTest(false);
        return;
      }

      const response = await fetchData<{
        message: string;
        results?: { client: string; success: boolean; error?: string }[];
      }>(`/api/webinar-mailings/campaigns/${localCampaignId}/send-test`, {
        method: 'POST',
        body: JSON.stringify({
          mailing_number: currentEmail.mailing_number,
          test_emails: emailList,
          client_codes: testClientCodes,
        }),
      });

      if (response.success) {
        setSuccess(response.data?.message || `Test-E-Mails erfolgreich gesendet`);
        setShowTestDialog(false);
        setTestEmails('');
        setTestClientCodes([]);
      } else {
        // Show partial success message if available
        const message =
          response.data?.message || response.error || 'Fehler beim Senden der Test-E-Mail';
        setError(message);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Senden der Test-E-Mail');
    } finally {
      setSendingTest(false);
    }
  };

  // Calculate scheduled datetime for an email (mirrors backend logic)
  const calculateScheduledDatetime = (
    email: EmailFormData,
    webinarStartDate: string,
  ): moment.Moment => {
    if (email.use_relative_date) {
      return moment
        .utc(webinarStartDate)
        .subtract(email.days_before_webinar || 0, 'days')
        .set({
          hour: parseInt((email.send_time || '09:00').split(':')[0]),
          minute: parseInt((email.send_time || '09:00').split(':')[1]),
          second: 0,
        });
    } else {
      return moment.utc(email.static_send_datetime);
    }
  };

  // Open schedule dialog with validation
  const handleSchedule = () => {
    setError(null);

    if (!localCampaignId) {
      setError('Bitte Kampagne erst speichern');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!selectedWebinar?.start_date) {
      setError('Webinar-Startdatum nicht verfügbar');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (emails.length === 0) {
      setError('Keine E-Mails konfiguriert');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setScheduleDialogOpen(true);
  };

  // Cancel schedules for a single email
  const handleCancelEmailSchedules = (emailIndex: number) => {
    setCancelEmailId(emailIndex);
    setCancelScope('email');
    setCancelDialogOpen(true);
  };

  // Cancel all schedules for entire campaign
  const handleCancelCampaignSchedules = () => {
    setCancelEmailId(null);
    setCancelScope('campaign');
    setCancelDialogOpen(true);
  };

  // Execute the cancel operation
  const executeCancelSchedules = async () => {
    if (!localCampaignId) return;

    setCancelling(true);
    setError(null);

    try {
      let response;
      if (cancelScope === 'email' && cancelEmailId !== null) {
        const emailNumber = cancelEmailId + 1; // Convert 0-indexed to 1-indexed mailing_number
        response = await fetchData(
          `/api/webinar-mailings/campaigns/${localCampaignId}/emails/${emailNumber}/schedules`,
          {
            method: 'DELETE',
          },
        );
      } else {
        response = await fetchData(
          `/api/webinar-mailings/campaigns/${localCampaignId}/all-schedules`,
          {
            method: 'DELETE',
          },
        );
      }

      if (response.success) {
        const resultData = response as unknown as {
          cancelled: number;
          skipped: number;
        };
        const cancelled = resultData.cancelled || 0;
        const skipped = resultData.skipped || 0;
        let message = `${cancelled} Planung(en) gecancelt`;
        if (skipped > 0) {
          message += `, ${skipped} übersprungen (bereits gesendet)`;
        }
        setSuccess(message);
        setCancelDialogOpen(false);
        loadCampaign(localCampaignId);
      } else {
        setError(response.error || 'Fehler beim Abbrechen');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCancelling(false);
    }
  };

  // Check if any email has active schedules (for campaign-level cancel button)
  const hasAnyActiveSchedules = emails.some((email) =>
    email.schedule_statuses?.split(',').some((pair) => {
      const status = pair.split(':')[1];
      return status === 'pending' || status === 'awaiting_suppression' || status === 'scheduled';
    }),
  );

  // Schedulability check for webinar emails
  const isWebinarEmailSchedulable = (email: {
    mailing_number: number;
    scheduled_datetime: string;
    selected_clients: string[];
    schedule_statuses?: string | null;
  }) => {
    if (!selectedWebinar?.start_date) {
      return { schedulable: false, reason: 'kein Webinar' };
    }

    // Find the full email data to check relative date settings
    const fullEmail = emails.find((e) => e.mailing_number === email.mailing_number);

    const hasClients = email.selected_clients?.length > 0;
    const hasValidDateConfig = fullEmail?.use_relative_date || fullEmail?.static_send_datetime;

    // Check for schedules already sent to ESP (scheduled = in ESP, sent = delivered)
    // Note: 'pending' means schedule record exists but NOT yet sent to ESP - this is reschedulable
    const hasEspSchedule =
      email.schedule_statuses?.split(',').some((pair) => {
        const status = pair.split(':')[1];
        return status === 'scheduled'; // Only block if actually scheduled in ESP
      }) || false;

    // Check if already sent
    const hasSent = email.schedule_statuses?.includes('sent');

    // All four values live in the Berlin-wallclock-as-UTC space that DB datetimes arrive in.
    // A plain moment() here would be real local time and skew the past-check by 1-2h.
    const now = germanNow();
    const minScheduleTime = now.clone().add(15, 'minutes');
    const webinarStart = moment.utc(selectedWebinar.start_date);
    const scheduledTime = moment.utc(email.scheduled_datetime);

    if (hasSent) return { schedulable: false, reason: 'bereits gesendet' };
    if (hasEspSchedule) return { schedulable: false, reason: 'bereits geplant' };
    if (!hasClients) return { schedulable: false, reason: 'keine Clients' };
    if (!hasValidDateConfig) return { schedulable: false, reason: 'kein Datum' };
    if (scheduledTime.isSameOrAfter(webinarStart))
      return { schedulable: false, reason: 'nach Webinar-Start' };
    if (scheduledTime.isBefore(minScheduleTime))
      return { schedulable: false, reason: 'Vergangenheit' };

    return { schedulable: true };
  };

  // Execute webinar scheduling
  const executeWebinarSchedule = async (emailNumbers: number[]) => {
    // Step 1: Auto-save all current changes
    const saveSuccess = await handleSave(true);
    if (!saveSuccess) {
      throw new Error('Speichern fehlgeschlagen - Planung abgebrochen');
    }

    // Step 2: Create schedule records in DB
    const scheduleRes = (await fetchData(
      `/api/webinar-mailings/campaigns/${localCampaignId}/schedule`,
      {
        method: 'POST',
        body: JSON.stringify({ email_numbers: emailNumbers }),
      },
    )) as {
      success: boolean;
      error?: string;
      skipped?: Array<{ message: string }>;
      scheduledScheduleIds?: number[];
    };

    // On a 4xx, fetchData keeps only `error` (axios throws, the rest of the body is dropped) —
    // so the backend puts the full per-email reason INTO that string. Don't reach for a
    // `conflicts` array here: it cannot survive the error path.
    if (!scheduleRes.success) {
      throw new Error(scheduleRes.error || 'Fehler beim Erstellen der Zeitpläne');
    }

    // Partial success: some (email, client) pairs were skipped. Stash it in a ref, not state —
    // the dialog calls onSuccess() in the same tick, so a state update would still be stale there.
    skippedNoteRef.current = scheduleRes.skipped?.length
      ? `${scheduleRes.skipped.length} übersprungen: ${scheduleRes.skipped.map((s) => s.message).join(' | ')}`
      : null;

    // Step 3: Send to ESP — but ONLY the rows this action just created. Passing the ids keeps
    // send-all from sweeping a stale/skipped pending row that shares the campaign (which would
    // send a mail the operator was just told was übersprungen).
    const createdIds = scheduleRes.scheduledScheduleIds ?? [];
    // Guard: never send an empty id list. The backend reads [] as "no filter → all pending", which
    // is exactly the sweep we're preventing. A 200 always creates ≥1 row, so this is just belt-and-
    // braces — if there is genuinely nothing new, there is nothing to send.
    if (createdIds.length === 0) {
      return;
    }
    const sendRes = await fetchData(
      `/api/webinar-mailings/campaigns/${localCampaignId}/send-all-to-esp`,
      {
        method: 'POST',
        body: JSON.stringify({ schedule_ids: createdIds }),
      },
    );

    if (!sendRes.success) {
      throw new Error(sendRes.error || 'Fehler beim Senden an ESP');
    }
  };

  // Reschedule handler - update ESP campaigns with latest content
  const handleReschedule = async (selectedClients: string[], selectedMailings: number[]) => {
    setShowRescheduleModal(false);
    setRescheduling(true);
    setError(null);

    try {
      const res = await fetchData(`/api/webinar-mailings/campaigns/${localCampaignId}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clients: selectedClients,
          mailingNumbers: selectedMailings,
        }),
      });

      if (res.success) {
        setSuccess(`Inhalte erfolgreich aktualisiert: ${(res as any).message || ''}`);
        loadCampaign(localCampaignId!);
      } else {
        throw new Error(res.error || 'Fehler beim Aktualisieren');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Aktualisieren der Inhalte');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setRescheduling(false);
    }
  };

  const updateEmail = (index: number, field: keyof EmailFormData, value: any) => {
    const newEmails = [...emails];
    newEmails[index] = { ...newEmails[index], [field]: value };
    setEmails(newEmails);
  };

  // Handle per-email mailing type change - updates that email's default clients
  const handleEmailTypeChange = (emailIndex: number, newType: MailingType) => {
    const newDefaultClients =
      newType === 'redlink' ? ['ft'] : clients.filter((c) => c.active).map((c) => c.code);

    setEmails((prev) =>
      prev.map((email, i) =>
        i === emailIndex
          ? {
              ...email,
              mailing_type: newType,
              selected_clients: newDefaultClients,
            }
          : email,
      ),
    );
  };

  const addEmail = () => {
    if (emails.length >= MAX_EMAILS_PER_CAMPAIGN) return;
    const newNumber = emails.length + 1;
    const newEmail = getDefaultEmail(clients, 'heatup'); // New emails default to heatup
    setEmails([
      ...emails,
      {
        ...newEmail,
        mailing_number: newNumber,
        days_before_webinar: Math.max(0, 7 - (newNumber - 1) * 2), // 7, 5, 3, 1, 0
      },
    ]);
    setActiveEmailTab(emails.length);
  };

  const removeEmail = (index: number) => {
    if (emails.length <= 1) return;
    const newEmails = emails.filter((_, i) => i !== index);
    // Renumber remaining emails
    newEmails.forEach((e, i) => (e.mailing_number = i + 1));
    setEmails(newEmails);
    setActiveEmailTab(Math.min(activeEmailTab, newEmails.length - 1));
  };

  // Editor mode change handler
  const handleEditorModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: EditorMode | null,
  ) => {
    if (newMode === null) return;

    // If switching to WYSIWYG and current email was imported, show warning
    if (newMode === 'wysiwyg' && importedEmails.has(activeEmailTab)) {
      setShowWysiwygWarning(true);
      return;
    }

    setEditorMode(newMode);
    localStorage.setItem('webinar-mailing-editor-mode', newMode);
  };

  // Handle confirmation to switch to WYSIWYG despite import warning
  const handleConfirmWysiwygSwitch = () => {
    setShowWysiwygWarning(false);
    setEditorMode('wysiwyg');
    localStorage.setItem('webinar-mailing-editor-mode', 'wysiwyg');
  };

  // Placeholder insertion handler
  const handleInsertPlaceholder = useCallback(
    (placeholder: string) => {
      if (editorMode === 'wysiwyg') {
        // Insert into TinyMCE (use active tab's editor ref)
        const editorRef = editorRefs.current[activeEmailTab];
        if (editorRef) {
          editorRef.insertContent(placeholder);
          editorRef.focus();
        }
      } else {
        // Insert into Monaco
        if (monacoRef.current) {
          const editor = monacoRef.current;
          const selection = editor.getSelection();
          const id = { major: 1, minor: 1 };
          const op = {
            identifier: id,
            range: selection,
            text: placeholder,
            forceMoveMarkers: true,
          };
          editor.executeEdits('insert-placeholder', [op]);
          editor.focus();
        }
      }
    },
    [editorMode, activeEmailTab],
  );

  // Debounced validation
  const runValidation = useCallback((subject: string, htmlBody: string) => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    validationTimeoutRef.current = setTimeout(() => {
      const result = validateEmail(subject, htmlBody);
      setValidation(result);
    }, 500);
  }, []);

  // Run validation when email content changes
  useEffect(() => {
    const currentEmail = emails[activeEmailTab];
    if (currentEmail) {
      runValidation(currentEmail.subject, currentEmail.html_body);
    }
  }, [emails, activeEmailTab, runValidation]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  // Derive scheduled items for the RescheduleModal
  // Parse schedule_statuses format: "ft:scheduled,be:sent,bg:scheduled"
  const scheduledItems = useMemo(() => {
    const items: { client: string; mailingNumber: number }[] = [];
    emails.forEach((email) => {
      if (email.schedule_statuses) {
        const pairs = email.schedule_statuses.split(',');
        pairs.forEach((pair) => {
          const [client, status] = pair.split(':');
          if (status === 'scheduled') {
            items.push({ client, mailingNumber: email.mailing_number });
          }
        });
      }
    });
    return items;
  }, [emails]);

  // Map client codes to display names
  const clientNames = useMemo(() => {
    const names: Record<string, string> = {};
    clients.forEach((c) => {
      names[c.code] = c.name;
    });
    return names;
  }, [clients]);

  const selectedWebinar = webinars.find((w) => w.webinar_id === webinarId);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
        <Typography variant="h6" gutterBottom>
          Kampagnen-Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kampagnen-Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. TAM Dezember 2025 Heat-Ups"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Webinar</InputLabel>
              <Select
                value={webinarId}
                label="Webinar"
                onChange={(e) => setWebinarId(e.target.value)}
              >
                {webinars.map((webinar) => (
                  <MenuItem key={webinar.webinar_id} value={webinar.webinar_id}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{webinar.title}</Typography>
                        {/* Bewerbungsstatus nur ANZEIGEN, nicht sperren — das Gate sitzt beim
                            Planen im Server; hier sieht man vorab, was es ablehnen wird. */}
                        {webinar.promo_label && (
                          <Chip
                            size="small"
                            label={webinar.promo_label}
                            color={webinar.promo_status === 'bewerbbar' ? 'success' : 'warning'}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {formatGermanTime(webinar.start_date, 'DD.MM.YYYY HH:mm')} | {webinar.type}
                        {webinar.is_evergreen && ' | Evergreen'}
                        {webinar.is_recurring && ' | Wiederkehrend'}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Occurrence date picker for recurring webinars */}
          {selectedWebinar && Boolean(selectedWebinar.is_recurring) && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Termin (Occurrence Date)"
                type="date"
                value={occurrenceDate}
                onChange={(e) => {
                  setOccurrenceDate(e.target.value);
                  // Auto-suggest campaign name
                  if (e.target.value && !localCampaignId) {
                    const formatted = moment(e.target.value).format('DD.MM.YYYY');
                    setName(`Heat-Up ${selectedWebinar.title} - ${formatted}`);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                helperText={(() => {
                  const days: number[] =
                    typeof selectedWebinar.recurrence_days === 'string'
                      ? JSON.parse(selectedWebinar.recurrence_days as unknown as string)
                      : selectedWebinar.recurrence_days || [];
                  const dayNames: Record<number, string> = {
                    1: 'Mo',
                    2: 'Di',
                    3: 'Mi',
                    4: 'Do',
                    5: 'Fr',
                    6: 'Sa',
                    7: 'So',
                  };
                  const allowedDays = days.map((d) => dayNames[d] || d).join(', ');
                  return `Erlaubte Tage: ${allowedDays}`;
                })()}
              />
            </Grid>
          )}

          {selectedWebinar && (
            <Grid item xs={12}>
              <Alert severity="info">
                <strong>Webinar-Datum:</strong>{' '}
                {Boolean(selectedWebinar.is_recurring) && occurrenceDate
                  ? moment(occurrenceDate).format('DD.MM.YYYY') +
                    ` um ${selectedWebinar.recurrence_time?.substring(0, 5) || '13:00'}`
                  : formatGermanTime(selectedWebinar.start_date, 'DD.MM.YYYY HH:mm')}
                {Boolean(selectedWebinar.is_recurring) && (
                  <Chip size="small" label="Wiederkehrend" color="warning" sx={{ ml: 1 }} />
                )}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Email Tabs */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            E-Mails ({emails.length}/{MAX_EMAILS_PER_CAMPAIGN})
          </Typography>
          {emails.length < MAX_EMAILS_PER_CAMPAIGN && (
            <Button size="small" startIcon={<AddIcon />} onClick={addEmail}>
              E-Mail hinzufügen
            </Button>
          )}
        </Box>

        <Tabs
          value={activeEmailTab}
          onChange={(_, newValue) => setActiveEmailTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {emails.map((email, index) => (
            <Tab
              key={index}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <span>Mailing #{email.mailing_number}</span>
                  {sentEmailIndices.has(index) ? (
                    <Tooltip title="Bereits versendet - kann nicht gelöscht werden">
                      <CheckCircleIcon fontSize="small" color="success" />
                    </Tooltip>
                  ) : scheduledEmailIndices.has(index) ? (
                    <>
                      <Tooltip title="Geplant - im Verlauf sichtbar">
                        <ScheduleIcon fontSize="small" color="warning" />
                      </Tooltip>
                      <Tooltip title="Planungen für diese E-Mail canceln">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelEmailSchedules(index);
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <CancelIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : null}
                  {emails.length > 1 && !sentEmailIndices.has(index) && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteEmailIndex(index);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>

        {emails.map((email, index) => (
          <TabPanel key={index} value={activeEmailTab} index={index}>
            <Grid container spacing={2}>
              {/* Per-Email Mailing Type Selector */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>E-Mail-Typ</InputLabel>
                  <Select
                    value={email.mailing_type || 'heatup'}
                    label="E-Mail-Typ"
                    onChange={(e) => handleEmailTypeChange(index, e.target.value as MailingType)}
                  >
                    <MenuItem value="heatup">
                      <Chip
                        size="small"
                        label="Heat-Up"
                        sx={{
                          backgroundColor: '#ff9800',
                          color: 'white',
                          mr: 1,
                        }}
                      />
                      Webinar-Promotion
                    </MenuItem>
                    <MenuItem value="redlink">
                      <Chip
                        size="small"
                        label="Redlink"
                        sx={{
                          backgroundColor: '#2196f3',
                          color: 'white',
                          mr: 1,
                        }}
                      />
                      Werbe-Mailing
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={email.subject}
                  onChange={(e) => updateEmail(index, 'subject', e.target.value)}
                  placeholder="E-Mail Betreff"
                  helperText={
                    containsHardcodedYear(email.subject)
                      ? YEAR_WARNING
                      : email.subject.length > 60
                        ? `${email.subject.length} Zeichen - wird auf Mobilgeräten abgeschnitten`
                        : `${email.subject.length}/60 Zeichen`
                  }
                  error={email.subject.length > 60 || containsHardcodedYear(email.subject)}
                />
              </Grid>

              {email.mailing_type === 'heatup' && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Betreff B (A/B-Test, optional)"
                    value={email.subject_b ?? ''}
                    onChange={(e) => updateEmail(index, 'subject_b', e.target.value)}
                    placeholder="Leer = kein Test"
                    helperText={
                      !(email.subject_b ?? '').trim()
                        ? 'Nur FT/Iterable: mit Betreff B wird die Welle beim Planen in zwei Hälften (Arm A/B) geteilt'
                        : containsHardcodedYear(email.subject_b ?? '')
                          ? YEAR_WARNING
                          : (email.subject_b ?? '').length > 60
                            ? `${(email.subject_b ?? '').length} Zeichen - wird auf Mobilgeräten abgeschnitten`
                            : `${(email.subject_b ?? '').length}/60 Zeichen · Arm B bekommt diesen Betreff, Arm A den Betreff oben`
                    }
                    error={
                      (email.subject_b ?? '').length > 60 ||
                      containsHardcodedYear(email.subject_b ?? '')
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="From Name (Standard)"
                  value={email.from_name}
                  onChange={(e) => updateEmail(index, 'from_name', e.target.value)}
                  helperText={
                    email.use_client_from_names
                      ? 'Wird als Fallback verwendet'
                      : 'Wird für alle Clients verwendet'
                  }
                />
              </Grid>

              {/* Client-specific From Names */}
              {email.selected_clients && email.selected_clients.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={email.use_client_from_names}
                          onChange={() => toggleUseClientFromNames(index)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Client-spezifische Absender-Namen
                        </Typography>
                      }
                    />
                  </Box>
                  {email.use_client_from_names && (
                    <Grid container spacing={2}>
                      {email.selected_clients.map((code) => {
                        const client = clients.find((c) => c.code === code);
                        const clientFromNames = getClientFromNames(index);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={code}>
                            <TextField
                              fullWidth
                              size="small"
                              label={client?.name || code}
                              value={clientFromNames[code] || ''}
                              onChange={(e) => updateClientFromName(index, code, e.target.value)}
                              placeholder={client?.name || code}
                              InputProps={{
                                startAdornment: (
                                  <Chip
                                    size="small"
                                    label={client?.esp_type === 'iterable' ? 'IT' : 'MC'}
                                    sx={{
                                      backgroundColor:
                                        client?.esp_type === 'iterable' ? '#6366f1' : '#ec4899',
                                      color: 'white',
                                      fontSize: '0.6rem',
                                      height: 20,
                                      mr: 1,
                                    }}
                                  />
                                ),
                              }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Auto Non-Opener — TEMPORARILY DEACTIVATED for webinar (2026-04-02) */}
              {false && email.selected_clients && email.selected_clients.length > 0 && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={email.auto_non_opener}
                        onChange={(e) => updateEmail(index, 'auto_non_opener', e.target.checked)}
                        disabled={sentEmailIndices.has(index) || scheduledEmailIndices.has(index)}
                      />
                    }
                    label="Automatischer Non-Opener Versand"
                  />
                  {email.auto_non_opener && (
                    <Box sx={{ mt: 1, ml: 2 }}>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <TextField
                          type="time"
                          label="Non-Opener Uhrzeit"
                          value={email.auto_non_opener_time}
                          onChange={(e) =>
                            updateEmail(index, 'auto_non_opener_time', e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          disabled={sentEmailIndices.has(index) || scheduledEmailIndices.has(index)}
                          sx={{ width: 160 }}
                        />
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={email.auto_non_opener_clients === null}
                            onChange={(e) =>
                              updateEmail(
                                index,
                                'auto_non_opener_clients',
                                e.target.checked ? null : [...email.selected_clients],
                              )
                            }
                            size="small"
                            disabled={
                              sentEmailIndices.has(index) || scheduledEmailIndices.has(index)
                            }
                          />
                        }
                        label="Alle ausgewählten Clients"
                      />
                      {email.auto_non_opener_clients !== null && (
                        <FormGroup row sx={{ ml: 2 }}>
                          {email.selected_clients.map((clientCode) => {
                            const client = clients.find((c) => c.code === clientCode);
                            return (
                              <FormControlLabel
                                key={clientCode}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={
                                      email.auto_non_opener_clients?.includes(clientCode) || false
                                    }
                                    onChange={(e) => {
                                      const current = email.auto_non_opener_clients || [];
                                      updateEmail(
                                        index,
                                        'auto_non_opener_clients',
                                        e.target.checked
                                          ? [...current, clientCode]
                                          : current.filter((c) => c !== clientCode),
                                      );
                                    }}
                                    disabled={
                                      sentEmailIndices.has(index) ||
                                      scheduledEmailIndices.has(index)
                                    }
                                  />
                                }
                                label={client?.name || clientCode}
                              />
                            );
                          })}
                        </FormGroup>
                      )}
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          Non-Opener wird automatisch am selben Tag nach dem Original-Versand
                          erstellt. FT (Iterable): Mindestens 10 Stunden Abstand wird automatisch
                          eingehalten.
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Preview Text"
                  value={email.preview_text}
                  onChange={(e) => updateEmail(index, 'preview_text', e.target.value)}
                  placeholder="Vorschau-Text für E-Mail-Client"
                  error={containsHardcodedYear(email.preview_text)}
                  helperText={containsHardcodedYear(email.preview_text) ? YEAR_WARNING : undefined}
                />
              </Grid>

              {/* Per-email Client Selection */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Ziel-Clients für diese E-Mail
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Clients auswählen</InputLabel>
                  <Select
                    multiple
                    value={email.selected_clients || []}
                    label="Clients auswählen"
                    onChange={(e) =>
                      updateEmail(index, 'selected_clients', e.target.value as string[])
                    }
                    renderValue={(selected) => (
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {selected.map((code) => {
                          const client = clients.find((c) => c.code === code);
                          return (
                            <Chip
                              key={code}
                              size="small"
                              label={client?.name || code}
                              sx={{
                                backgroundColor:
                                  client?.esp_type === 'iterable' ? '#6366f1' : '#ec4899',
                                color: 'white',
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {clients
                      .filter((c) => c.active)
                      .map((client) => (
                        <MenuItem key={client.code} value={client.code}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Checkbox
                              checked={(email.selected_clients || []).indexOf(client.code) > -1}
                              size="small"
                            />
                            <Chip
                              size="small"
                              label={client.esp_type === 'iterable' ? 'Iterable' : 'Mailchimp'}
                              sx={{
                                backgroundColor:
                                  client.esp_type === 'iterable' ? '#6366f1' : '#ec4899',
                                color: 'white',
                                fontSize: '0.7rem',
                              }}
                            />
                            {client.name}
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                {email.mailing_type === 'redlink' &&
                  (email.selected_clients || []).some((c) => c !== 'ft') && (
                    <Alert severity="info" sx={{ mt: 1 }} icon={<WarningIcon />}>
                      Redlink-Mailings werden normalerweise nur an Finanztrends (FT) versendet.
                    </Alert>
                  )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Versand-Zeitpunkt
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Datum-Modus</InputLabel>
                  <Select
                    value={email.use_relative_date ? 'relative' : 'static'}
                    label="Datum-Modus"
                    onChange={(e) =>
                      updateEmail(index, 'use_relative_date', e.target.value === 'relative')
                    }
                  >
                    <MenuItem value="relative">Relativ zum Webinar</MenuItem>
                    <MenuItem value="static">Festes Datum</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {email.use_relative_date ? (
                <>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Tage vor Webinar</InputLabel>
                      <Select
                        value={email.days_before_webinar || 0}
                        label="Tage vor Webinar"
                        onChange={(e) =>
                          updateEmail(index, 'days_before_webinar', Number(e.target.value))
                        }
                      >
                        {[14, 10, 7, 5, 3, 2, 1, 0].map((days) => (
                          <MenuItem key={days} value={days}>
                            {days === 0 ? 'Am Webinar-Tag' : `${days} Tage vorher`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Uhrzeit"
                      type="time"
                      value={email.send_time || '09:00'}
                      onChange={(e) => updateEmail(index, 'send_time', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {selectedWebinar && (
                    <Grid item xs={12}>
                      <Alert severity="info" icon={<ScheduleIcon />}>
                        Geplanter Versand:{' '}
                        {moment
                          .utc(selectedWebinar.start_date)
                          .subtract(email.days_before_webinar || 0, 'days')
                          .format('DD.MM.YYYY')}{' '}
                        um {email.send_time || '09:00'}
                      </Alert>
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Versand-Datum und Uhrzeit (MEZ/MESZ)"
                    type="datetime-local"
                    value={
                      email.static_send_datetime
                        ? toGermanDatetimeLocal(email.static_send_datetime)
                        : ''
                    }
                    onChange={(e) => updateEmail(index, 'static_send_datetime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />

                {/* Placeholder Legend */}
                <PlaceholderLegend
                  items={PLACEHOLDERS}
                  onInsert={handleInsertPlaceholder}
                  disabled={false}
                  hint="Hinweis: Der Anmelde-Link muss im E-Mail-Inhalt enthalten sein. Andere Links sind nicht erlaubt."
                />

                {/* Editor Mode Toggle & Import Button */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      HTML-Body
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UploadFileIcon />}
                      onClick={handleOpenImportModal}
                    >
                      Aus HTML Template importieren
                    </Button>
                  </Box>
                  <ToggleButtonGroup
                    value={editorMode}
                    exclusive
                    onChange={handleEditorModeChange}
                    size="small"
                  >
                    <ToggleButton value="wysiwyg" sx={{ px: 2 }}>
                      <EditIcon sx={{ mr: 1, fontSize: 18 }} />
                      Visual
                    </ToggleButton>
                    <ToggleButton value="code" sx={{ px: 2 }}>
                      <CodeIcon sx={{ mr: 1, fontSize: 18 }} />
                      HTML-Code
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Editor - TinyMCE or Monaco */}
                {editorMode === 'wysiwyg' ? (
                  <TinyMCEEditor
                    ref={(el) => {
                      editorRefs.current[index] = el;
                    }}
                    value={email.html_body}
                    onChange={(content) => updateEmail(index, 'html_body', content)}
                    height={500}
                    placeholder="HTML-Inhalt der E-Mail..."
                  />
                ) : (
                  <Box
                    sx={{
                      border: '1px solid #ccc',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Editor
                      height={500}
                      defaultLanguage="html"
                      value={email.html_body}
                      onChange={(value) => updateEmail(index, 'html_body', value || '')}
                      onMount={(editor) => {
                        monacoRef.current = editor;
                      }}
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: false },
                        wordWrap: 'on',
                        lineNumbers: 'on',
                        fontSize: 13,
                        tabSize: 2,
                        scrollBeyondLastLine: false,
                      }}
                    />
                  </Box>
                )}

                {/* Validation Status */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 1,
                    backgroundColor:
                      validation.valid && validation.warnings.length === 0
                        ? '#e8f5e9'
                        : validation.valid
                          ? '#fff3e0'
                          : '#ffebee',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {validation.valid && validation.warnings.length === 0 ? (
                      <>
                        <CheckCircleIcon sx={{ color: '#4caf50' }} />
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                          Alle Prüfungen bestanden
                        </Typography>
                      </>
                    ) : validation.valid ? (
                      <>
                        <WarningIcon sx={{ color: '#ff9800' }} />
                        <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                          Hinweise vorhanden
                        </Typography>
                      </>
                    ) : (
                      <>
                        <ErrorIcon sx={{ color: '#f44336' }} />
                        <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                          Validierungsfehler
                        </Typography>
                      </>
                    )}
                  </Box>

                  {validation.errors.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {validation.errors.map((err, i) => (
                        <Typography key={i} variant="body2" sx={{ color: '#c62828', ml: 4 }}>
                          • {err}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {validation.warnings.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {validation.warnings.map((warn, i) => (
                        <Typography key={i} variant="body2" sx={{ color: '#e65100', ml: 4 }}>
                          • {warn}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Spam-Score / Content-Quality Panel */}
                <SpamScorePanel
                  editorType="webinar_campaign"
                  referenceId={email.mailing_number ?? null}
                  campaignId={localCampaignId ?? null}
                  subject={email.subject || ''}
                  preheader={email.preview_text || ''}
                  body={email.html_body || ''}
                  fromName={email.from_name || ''}
                  onResultChange={(r) => handleSpamResultChange(index, r)}
                />
              </Grid>

              {/* Preview and Test Email Buttons */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
                  <Tooltip title="Vorschau mit ersetzten Platzhaltern anzeigen">
                    <span>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={
                          loadingPreview ? <CircularProgress size={20} /> : <VisibilityIcon />
                        }
                        onClick={handlePreview}
                        disabled={loadingPreview || !localCampaignId || !email.html_body}
                      >
                        {loadingPreview ? 'Lade...' : 'Vorschau'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Test-E-Mail an sich selbst senden">
                    <span>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<SendIcon />}
                        onClick={() => {
                          setTestClientCodes(emails[activeEmailTab]?.selected_clients || []);
                          setShowTestDialog(true);
                        }}
                        disabled={!localCampaignId || !email.subject || !email.html_body}
                      >
                        Test senden
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
        ))}
      </Paper>

      {/* Non-Opener Section — TEMPORARILY DEACTIVATED for webinar (2026-04-02) */}
      {false && localCampaignId && emails.some((e) => e.schedule_statuses) && (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
          <NonOpenerSection
            systemType="webinar"
            campaignId={localCampaignId}
            emails={emails.map((e) => ({
              mailing_number: e.mailing_number,
              subject: e.subject,
              schedule_statuses: e.schedule_statuses || null,
              selected_clients: e.selected_clients,
            }))}
            onNonOpenerScheduled={() => loadCampaign(localCampaignId!)}
          />
        </Paper>
      )}

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between">
        <Button variant="outlined" onClick={onCancel}>
          Abbrechen
        </Button>

        <Box display="flex" gap={2}>
          <Tooltip title={!validation.valid ? 'Bitte beheben Sie die Validierungsfehler' : ''}>
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={() => handleSave()}
                disabled={saving || !validation.valid}
              >
                {saving ? <CircularProgress size={24} /> : 'Speichern'}
              </Button>
            </span>
          </Tooltip>

          {localCampaignId && (
            <Tooltip title="Kampagne planen und an ESPs senden">
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ScheduleIcon />}
                  onClick={handleSchedule}
                  disabled={
                    saving ||
                    !emails.some((e) => e.selected_clients && e.selected_clients.length > 0)
                  }
                >
                  Planen
                </Button>
              </span>
            </Tooltip>
          )}

          {localCampaignId && hasScheduledItems && (
            <Tooltip title="Geplante Sendungen mit aktuellem Inhalt aktualisieren">
              <span>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={rescheduling ? <CircularProgress size={20} /> : <EditIcon />}
                  onClick={() => setShowRescheduleModal(true)}
                  disabled={saving || rescheduling}
                >
                  Inhalte aktualisieren
                </Button>
              </span>
            </Tooltip>
          )}

          {localCampaignId && hasAnyActiveSchedules && (
            <Tooltip title="Alle Planungen für diese Kampagne canceln">
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelCampaignSchedules}
                  disabled={saving || cancelling}
                >
                  Alle abbrechen
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Schedule Confirmation Dialog - using shared component */}
      <ScheduleConfirmationDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        system="webinar"
        policy={mailingPolicy}
        minSendTime={minSendTime}
        deferredHeatupClients={deferredHeatupClients}
        emails={emails.map((e) => ({
          mailing_number: e.mailing_number,
          mailing_type: e.mailing_type,
          subject: e.subject,
          scheduled_datetime: selectedWebinar?.start_date
            ? calculateScheduledDatetime(e, selectedWebinar.start_date).toISOString()
            : '',
          selected_clients: e.selected_clients,
          from_name: e.from_name,
          use_client_from_names: e.use_client_from_names,
          client_from_names: e.client_from_names,
          schedule_statuses: e.schedule_statuses,
        }))}
        clients={clients.map((c) => ({
          code: c.code,
          name: c.name,
          esp_type: c.esp_type,
        }))}
        campaignName={name}
        subtitle={`Webinar: ${selectedWebinar?.title || '-'}`}
        isSchedulable={isWebinarEmailSchedulable}
        formatScheduledTime={(email) => formatGermanTime(email.scheduled_datetime, 'DD.MM. HH:mm')}
        onSchedule={executeWebinarSchedule}
        onSuccess={() => {
          setScheduleDialogOpen(false);
          const note = skippedNoteRef.current;
          skippedNoteRef.current = null;
          setSuccess(
            note
              ? `Kampagne geplant — ESP-Versand läuft im Hintergrund. ${note}`
              : 'Kampagne geplant — ESP-Versand läuft im Hintergrund',
          );
          loadCampaign(localCampaignId!);
        }}
        showIterableWarning
      />

      {/* Reschedule Modal with Client/Mailing Selection */}
      <RescheduleModal
        open={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        onConfirm={handleReschedule}
        loading={rescheduling}
        scheduledItems={scheduledItems}
        clientNames={clientNames}
      />

      {/* Test Email Dialog */}
      <Dialog
        open={showTestDialog}
        onClose={() => setShowTestDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SendIcon color="secondary" />
            Test-E-Mail senden
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Sendet Mailing #{emails[activeEmailTab]?.mailing_number} als Test-E-Mail an die
            ausgewählten Clients.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="E-Mail-Adressen"
                value={testEmails}
                onChange={(e) => setTestEmails(e.target.value)}
                placeholder="email1@example.de, email2@example.de"
                helperText="Mehrere E-Mail-Adressen mit Komma trennen"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Clients für Test auswählen:
            </Typography>
            {!emails[activeEmailTab]?.selected_clients ||
            emails[activeEmailTab]?.selected_clients?.length === 0 ? (
              <Typography variant="body2" color="error">
                Keine Clients für diese E-Mail konfiguriert. Bitte wählen Sie Clients im Editor.
              </Typography>
            ) : (
              <FormGroup>
                {(emails[activeEmailTab]?.selected_clients || []).map((clientCode: string) => {
                  const client = clients.find((c) => c.code === clientCode);
                  return client ? (
                    <FormControlLabel
                      key={clientCode}
                      control={
                        <Checkbox
                          checked={testClientCodes.includes(clientCode)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTestClientCodes([...testClientCodes, clientCode]);
                            } else {
                              setTestClientCodes(testClientCodes.filter((c) => c !== clientCode));
                            }
                          }}
                          sx={{
                            color: client.esp_type === 'iterable' ? '#6366f1' : '#ec4899',
                            '&.Mui-checked': {
                              color: client.esp_type === 'iterable' ? '#6366f1' : '#ec4899',
                            },
                          }}
                        />
                      }
                      label={`${client.name} (${client.esp_type === 'iterable' ? 'Iterable' : 'Mailchimp'})`}
                    />
                  ) : null;
                })}
              </FormGroup>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTestDialog(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSendTestEmail}
            disabled={sendingTest || !testEmails.trim() || testClientCodes.length === 0}
            startIcon={sendingTest ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingTest ? 'Sende...' : 'Test senden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VisibilityIcon color="primary" />
            E-Mail-Vorschau
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Vorschau mit ersetzten Platzhaltern. So sieht die E-Mail für Empfänger aus.
          </Typography>

          {/* Dynamic Date Legend */}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: '#e3f2fd',
              borderRadius: 1,
              border: '1px solid #90caf9',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
              Dynamische Datumsangaben
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Platzhalter wie <code>{'{WEBINAR_DATE_DYNAMIC}'}</code> zeigen das Datum relativ zum{' '}
              <strong>geplanten Versandzeitpunkt</strong>, nicht zum aktuellen Zeitpunkt.
              &quot;Heute&quot; bedeutet: Am Tag, an dem der Empfänger die E-Mail erhält, findet das
              Webinar statt.
            </Typography>
          </Box>

          {/* Client & View Controls */}
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Client-Vorschau</InputLabel>
              <Select
                value={previewClient}
                onChange={(e) => setPreviewClient(e.target.value)}
                label="Client-Vorschau"
              >
                {clients.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup
              value={previewWidth}
              exclusive
              onChange={(_, v) => v && setPreviewWidth(v)}
              size="small"
            >
              <ToggleButton value="desktop">
                <DesktopWindowsIcon sx={{ mr: 0.5 }} /> Desktop (640px)
              </ToggleButton>
              <ToggleButton value="mobile">
                <PhoneIphoneIcon sx={{ mr: 0.5 }} /> Mobile (375px)
              </ToggleButton>
            </ToggleButtonGroup>

            {loadingPreview && <CircularProgress size={20} />}
          </Box>

          {/* Subject Preview */}
          {emails[activeEmailTab] && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Betreff:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {emails[activeEmailTab].subject}
              </Typography>
            </Box>
          )}

          {/* HTML Preview in iframe - responsive container */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: '#e0e0e0',
              p: 2,
              borderRadius: 1,
              minHeight: 520,
              overflow: 'auto',
            }}
          >
            <iframe
              srcDoc={previewHtml}
              title="E-Mail Vorschau"
              style={{
                width: previewWidth === 'desktop' ? '640px' : '375px',
                height: '500px',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                flexShrink: 0,
              }}
              sandbox="allow-same-origin"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewDialog(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Import Template Modal */}
      <Dialog open={importModalOpen} onClose={handleCloseImportModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <UploadFileIcon color="primary" />
            Aus HTML Template importieren
          </Box>
        </DialogTitle>
        <DialogContent>
          {importError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>
              {importError}
            </Alert>
          )}

          {/* File Upload Section */}
          {!importedContent && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Wählen Sie eine HTML-Datei aus einem ESP-Export (Iterable, Mailchimp). Der
                E-Mail-Inhalt wird automatisch extrahiert.
              </Typography>

              <input
                ref={fileInputRef}
                type="file"
                accept=".html,.htm"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              <Button
                variant="contained"
                startIcon={
                  importLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <UploadFileIcon />
                  )
                }
                onClick={() => fileInputRef.current?.click()}
                disabled={importLoading}
              >
                {importLoading ? 'Wird analysiert...' : 'HTML-Datei auswählen'}
              </Button>
            </Box>
          )}

          {/* Content Preview & URL Selection */}
          {importedContent && (
            <Box sx={{ py: 2 }}>
              {/* URL Selection */}
              {detectedUrls.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Erkannte URLs ({detectedUrls.length})
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Wählen Sie die URLs aus, die durch {'{WEBINAR_LINK}'} ersetzt werden sollen:
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflow: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {detectedUrls.map((url, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={
                          <Checkbox
                            checked={selectedUrls.includes(url)}
                            onChange={() => handleToggleUrl(url)}
                            size="small"
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ wordBreak: 'break-all', fontSize: '0.85rem' }}
                          >
                            {url.length > 80 ? url.substring(0, 77) + '...' : url}
                          </Typography>
                        }
                        sx={{ display: 'block', mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {detectedUrls.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Keine Webinar-URLs im Inhalt gefunden. Der Inhalt wird ohne URL-Ersetzung
                  importiert.
                </Alert>
              )}

              {/* Content Preview */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Extrahierter Inhalt
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  maxHeight: 300,
                  overflow: 'auto',
                  p: 2,
                  backgroundColor: '#fafafa',
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: importedContent }} />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportModal}>Abbrechen</Button>
          {importedContent && (
            <Button variant="contained" color="primary" onClick={handleConfirmImport}>
              Importieren{' '}
              {selectedUrls.length > 0 &&
                `(${selectedUrls.length} URL${selectedUrls.length > 1 ? 's' : ''} ersetzen)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* WYSIWYG Warning Dialog (for imported templates) */}
      <Dialog open={showWysiwygWarning} onClose={() => setShowWysiwygWarning(false)} maxWidth="sm">
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            Formatierung könnte verloren gehen
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Der importierte HTML-Code enthält möglicherweise Styles die TinyMCE nicht vollständig
            unterstützt (z.B. background-color, floats, komplexe Layouts).
          </Typography>
          <Typography sx={{ mt: 2 }}>Wechsel trotzdem zum Visual-Editor?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWysiwygWarning(false)}>Abbrechen</Button>
          <Button onClick={handleConfirmWysiwygSwitch} color="warning" variant="contained">
            Trotzdem wechseln
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Email Confirmation Dialog */}
      <Dialog
        open={deleteEmailIndex !== null}
        onClose={() => setDeleteEmailIndex(null)}
        maxWidth="xs"
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            E-Mail löschen?
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Mailing #{deleteEmailIndex !== null ? deleteEmailIndex + 1 : ''} wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEmailIndex(null)}>Abbrechen</Button>
          <Button
            onClick={() => {
              if (deleteEmailIndex !== null) {
                removeEmail(deleteEmailIndex);
                setDeleteEmailIndex(null);
              }
            }}
            color="error"
            variant="contained"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Schedules Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>
          {cancelScope === 'campaign'
            ? 'Alle Planungen canceln?'
            : `Planungen für E-Mail ${cancelEmailId !== null ? cancelEmailId + 1 : ''} canceln?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cancelScope === 'campaign'
              ? 'Alle geplanten Versendungen für diese Kampagne werden in den ESPs abgebrochen und aus der Datenbank gelöscht. Bereits versendete E-Mails bleiben erhalten.'
              : 'Alle geplanten Versendungen für diese E-Mail werden in den ESPs abgebrochen und aus der Datenbank gelöscht. Bereits versendete E-Mails bleiben erhalten.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={executeCancelSchedules}
            color="error"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? 'Wird abgebrochen...' : 'Planungen canceln'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spam-Score Hard-Block Override Dialog */}
      <Dialog
        open={spamOverrideOpen}
        onClose={() => {
          spamOverridePayload?.resolve(false);
          setSpamOverrideOpen(false);
          setSpamOverridePayload(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#dc2626', fontWeight: 'bold' }}>
          ⚠️ Content-Quality-Check: kritischer Score
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {spamOverridePayload != null && (
              <>
                E-Mail {spamOverridePayload.emailIndex + 1} enthält{' '}
                <strong>
                  {spamResults[spamOverridePayload.emailIndex]?.layer1.redCount ?? 0}{' '}
                  Red-Flag-Wörter
                </strong>
                . Diese Kombination erhöht das Risiko als Spam eingestuft zu werden spürbar.
                <br />
                <br />
                Du kannst die Mail trotzdem speichern — die Entscheidung wird zu Auswertungs-Zwecken
                protokolliert. Alternativ: Text überarbeiten und neu speichern.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              spamOverridePayload?.resolve(false);
              setSpamOverrideOpen(false);
              setSpamOverridePayload(null);
            }}
          >
            Abbrechen & überarbeiten
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              spamOverridePayload?.resolve(true);
              setSpamOverrideOpen(false);
              setSpamOverridePayload(null);
            }}
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spam-Score Advisory Dialog (non-blocking) */}
      <Dialog
        open={spamAdvisoryOpen}
        onClose={() => {
          spamAdvisoryPayload?.resolve(false);
          setSpamAdvisoryOpen(false);
          setSpamAdvisoryPayload(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#f97316', fontWeight: 'bold' }}>
          💡 Content-Hinweise vor dem Speichern
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {spamAdvisoryPayload != null && (
              <>
                E-Mail {spamAdvisoryPayload.emailIndex + 1} hat{' '}
                <strong>
                  {spamAdvisoryPayload.warningCount} Content-Hinweis
                  {spamAdvisoryPayload.warningCount === 1 ? '' : 'e'}
                </strong>{' '}
                (z.B. Red-Flag-Wörter, Urgency-Muster oder Caps-Runs).
                <br />
                <br />
                <strong>Empfehlung vor dem Versand:</strong>
                <ol style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                  <li>Die markierten Hinweise im Content-Quality-Panel prüfen und bereinigen.</li>
                  {!spamAdvisoryPayload.llmChecked && (
                    <li>
                      <strong>LLM-Check starten</strong> — liefert kontextuelle Analyse + konkrete
                      Umformulierungs-Vorschläge.
                    </li>
                  )}
                  {spamAdvisoryPayload.llmChecked && (
                    <li>LLM-Rewrite-Vorschläge übernehmen und danach Check wiederholen.</li>
                  )}
                </ol>
                Bei niedriger Absender-Reputation erhöhen diese Signale das Spam-Risiko bei Gmail
                und GMX. Du kannst trotzdem speichern — die Entscheidung wird protokolliert.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              spamAdvisoryPayload?.resolve(false);
              setSpamAdvisoryOpen(false);
              setSpamAdvisoryPayload(null);
            }}
          >
            Zurück & überarbeiten
          </Button>
          <Button
            onClick={() => {
              spamAdvisoryPayload?.resolve(true);
              setSpamAdvisoryOpen(false);
              setSpamAdvisoryPayload(null);
            }}
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignEditor;
